import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/lib/shadcn/components/ui/button';
import { Input } from '@/lib/shadcn/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/lib/shadcn/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/lib/shadcn/components/ui/form';
import { SharedAlertDialog, useAlertDialog } from '@/shared/components';
import { CreateCustomerFormProps, CustomerFormData } from '../../models';
import { UseCustomerFormSchema } from '../../schema/customer.schema';
import { Checkbox } from '@/lib/shadcn/components/ui';
import { t } from 'i18next';

export const CustomerForm = ({
  initialData,
  onSubmit,
  isLoading = false,
  mode,
  translate,
  error,
  onDirtyChange,
}: CreateCustomerFormProps & {
  onDirtyChange?: (isDirty: boolean) => void;
}) => {
  const { customerFormSchema } = UseCustomerFormSchema();

  const form = useForm<CustomerFormData>({
    resolver: zodResolver(customerFormSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: initialData?.firstName ?? '',
      lastName: initialData?.lastName ?? '',
      streetAddress: initialData?.streetAddress ?? '',
      zipCode: initialData?.zipCode ?? '',
      city: initialData?.city ?? '',
      email: initialData?.email ?? '',
      energyProviderName: initialData?.energyProviderName ?? '',
      energyProviderCustomerId: initialData?.energyProviderCustomerId ?? '',
      energyProviderEmail: initialData?.energyProviderEmail ?? '',
      ...(mode === 'edit' && {
        isPayoutDisabled: initialData?.isPayoutDisabled ?? false,
        isYearlyBillDisabled: initialData?.isYearlyBillDisabled ?? false,
      }),
    },
  });

  const { isOpen: isAlertOpen, showAlert, hideAlert } = useAlertDialog();

  const handleSubmit = (data: CustomerFormData) => {
    if (mode === 'edit') {
      showAlert();
      return;
    }
    submitForm(data);
  };

  useEffect(() => {
    onDirtyChange?.(form.formState.isDirty);
  }, [form.formState.isDirty, onDirtyChange]);

  const submitForm = (data: CustomerFormData) => {
    const cleanedData = {
      ...data,
      firstName: data.firstName.trim(),
      lastName: (data.lastName ?? '').trim(),
      email: data.email.trim(),
      energyProviderEmail: data.energyProviderEmail
        ? data.energyProviderEmail.trim()
        : undefined,
      energyProviderName: data.energyProviderName?.trim(),
      customerNumber: data.energyProviderCustomerId?.trim(),
      streetAddress: (data.streetAddress ?? '').trim(),
      zipCode: (data.zipCode ?? '').trim(),
      city: (data.city ?? '').trim(),
      isPayoutDisabled: data.isPayoutDisabled,
      isYearlyBillDisabled: data.isYearlyBillDisabled,
    };
    onSubmit(cleanedData);
  };

  const handleConfirmUpdate = () => {
    submitForm(form.getValues());
    hideAlert();
  };

  const isFormValid = form.formState.isValid;

  const getButtonLabel = () => {
    if (isLoading) {
      return mode === 'create'
        ? translate('form.creating')
        : translate('form.updating');
    }
    return mode === 'create'
      ? translate('form.createCustomer')
      : translate('form.updateCustomer');
  };

  return (
    <>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* First Name */}
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* {translate('form.firstName')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={translate('form.enterFirstName')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Last Name */}
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* {translate('form.lastName')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={translate('form.enterLastName')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* {translate('form.email')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={translate('form.enterEmail')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Energy Provider section moved to bottom for layout */}

            {/* Address - Street */}
            <FormField
              control={form.control}
              name="streetAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* {translate('form.address')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={translate('form.enterAddress')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Postal Code */}
            <FormField
              control={form.control}
              name="zipCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* {translate('form.postalCode')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={translate('form.enterPostCode')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* City */}
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>* {translate('form.city')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={translate('form.enterCity')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Payout Enabled Dropdown */}
            {mode === 'edit' && (
              <FormField
                control={form.control}
                name="isPayoutDisabled"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate('form.disablePayout')}</FormLabel>
                    <FormControl>
                      <Select
                        // "Yes" --> disable payout button in app
                        // "No" --> keep payout button enabled in app
                        onValueChange={value => field.onChange(value === 'Yes')}
                        value={field.value ? 'Yes' : 'No'}
                      >
                        <SelectTrigger className="w-[150px]">
                          <SelectValue
                            placeholder={translate('form.disablePayout')}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">
                            {translate('form.disable')}
                          </SelectItem>
                          <SelectItem value="No">
                            {translate('form.notDisable')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
              <FormField
                control={form.control}
                name="isYearlyBillDisabled"
                render={({ field }) => (
                  <div className="flex items-center gap-2 px-2">
                    <Checkbox
                      id="yearly-bill-disabled"
                      checked={field.value}
                      onCheckedChange={value => field.onChange(!!value)}
                    />
                    <label
                      htmlFor="yearly-bill-disabled"
                      className="text-sm font-medium"
                    >
                      {t('form.disableYearlyInvoice', 'Disable yearly invoice')}
                    </label>
                  </div>
                )}
              />

            {/* Energy Provider Name */}
            {/* Energy Provider Name & Customer Number moved into the Energy Provider section above */}
          </div>

          {/* Energy Provider section (moved to bottom for better layout) */}
          <fieldset className="space-y-2 border p-4 rounded">
            <legend className="text-sm font-medium">Energy provider</legend>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="energyProviderName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {translate('form.energyProviderName')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={translate('form.energyProviderName')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="energyProviderCustomerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate('form.customerNumber')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={translate('form.enterCustomerNumber')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="energyProviderEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate('form.providerEmail')}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={translate('form.enterProviderEmail')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </fieldset>

          {/* Form Actions */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isLoading || !isFormValid}
              className="min-w-[140px]"
            >
              {getButtonLabel()}
            </Button>
          </div>
        </form>
      </Form>

      {/* Update Confirmation Dialog */}
      <SharedAlertDialog
        open={isAlertOpen}
        onOpenChange={hideAlert}
        title="Update Customer"
        description="Are you sure you want to update this customer's information?"
        confirmText="Update Customer"
        cancelText="Cancel"
        onConfirm={handleConfirmUpdate}
      />
    </>
  );
};
